import {useState, ChangeEvent, useEffect} from 'react';
import {TransitionModal} from './transition-modal';
import {TEXT_INPUT_STYLE, BUTTON_STYLE_CANCEL_FLEX, BUTTON_STYLE_MAIN_FLEX} from './styles';

type ConstraintsInputsProps = {
  label: string;
  initialConfig?: ConstraintNumber;
  setConstraints: (constraints: ConstraintNumber) => void;
};

function ConstraintsInputs({label, initialConfig, setConstraints}: ConstraintsInputsProps) {
  const initialMax = initialConfig?.max ? String(initialConfig.max) : '';
  const initialMin = initialConfig?.min ? String(initialConfig.min) : '';
  const initialExact = initialConfig?.exact ? String(initialConfig.exact) : '';
  const initialIdeal = initialConfig?.ideal ? String(initialConfig.ideal) : '';

  const [max, setMax] = useState(initialMax);
  const [min, setMin] = useState(initialMin);
  const [exact, setExact] = useState(initialExact);
  const [ideal, setIdeal] = useState(initialIdeal);

  function onChange(setter: (value: string) => void, e: ChangeEvent<HTMLInputElement>) {
    setter(e.target.value);
  }

  useEffect(() => {
    setConstraints({
      max: max.length === 0 ? undefined : Number(max),
      min: min.length === 0 ? undefined : Number(min),
      exact: exact.length === 0 ? undefined : Number(exact),
      ideal: ideal.length === 0 ? undefined : Number(ideal),
    });
  }, [max, min, exact, ideal]);

  return (
    <>
      <div className="mr-1 mb-1 font-bold">{label}</div>
      <div className="mt-2 text-sm grid grid-cols-4">
        <div className="mr-1 mb-1">
          <span>min</span>
          <input className={TEXT_INPUT_STYLE} value={min} onChange={e => onChange(setMin, e)} />
        </div>{' '}
        <div className="mr-1 mb-1">
          <span>max</span>
          <input className={TEXT_INPUT_STYLE} value={max} onChange={e => onChange(setMax, e)} />
        </div>
        <div className="mr-1 mb-1">
          <span>exact</span>
          <input className={TEXT_INPUT_STYLE} value={exact} onChange={e => onChange(setExact, e)} />
        </div>
        <div className="mr-1 mb-1">
          <span>ideal</span>
          <input className={TEXT_INPUT_STYLE} value={ideal} onChange={e => onChange(setIdeal, e)} />
        </div>
      </div>
    </>
  );
}

type ConstraintsModalProps = {
  isOpen: boolean;
  initialConfig: MediaTrackConstraints | undefined;
  setConstraintsCallback: (constraints: MediaTrackConstraints) => void;
  setOpen: (isOpen: boolean) => void;
};

type ConstraintNumber = {
  max: number | undefined;
  min: number | undefined;
  exact: number | undefined;
  ideal: number | undefined;
};

export function ConstraintsModal({isOpen, initialConfig, setConstraintsCallback, setOpen}: ConstraintsModalProps) {
  const initialWidth = convertToConstraintNumber(initialConfig?.width);
  const initialHeight = convertToConstraintNumber(initialConfig?.height);
  const initialFrameRate = convertToConstraintNumber(initialConfig?.frameRate);

  const [width, setWidth] = useState<ConstraintNumber>(initialWidth);
  const [height, setHeight] = useState<ConstraintNumber>(initialHeight);
  const [frameRate, setFrameRate] = useState<ConstraintNumber>(initialFrameRate);

  function convertToConstraintNumber(constraint: ConstrainDouble | ConstrainULong | undefined): ConstraintNumber {
    if (typeof constraint === 'object') {
      return {
        max: constraint.max,
        min: constraint.min,
        exact: constraint.exact,
        ideal: constraint.ideal,
      };
    } else {
      return {
        max: undefined,
        min: undefined,
        exact: undefined,
        ideal: undefined,
      };
    }
  }

  function cancel(): void {
    setWidth(initialWidth);
    setHeight(initialHeight);
    setFrameRate(initialFrameRate);
    setOpen(false);
  }

  function isConstraintUndefined(c: ConstraintNumber): boolean {
    return c.max === undefined && c.min === undefined && c.exact === undefined && c.ideal === undefined;
  }

  function submit() {
    const constraints: MediaTrackConstraints = {
      frameRate: isConstraintUndefined(frameRate) ? undefined : frameRate,
      width: isConstraintUndefined(width) ? undefined : width,
      height: isConstraintUndefined(height) ? undefined : height,
    };

    setConstraintsCallback(constraints);
    setOpen(false);
  }

  return (
    <TransitionModal isOpen={isOpen} onClose={cancel} title="Video Constraints Configuration">
      <ConstraintsInputs label="Width" initialConfig={width} setConstraints={setWidth} />
      <ConstraintsInputs label="Height" initialConfig={height} setConstraints={setHeight} />
      <ConstraintsInputs label="Frame Rate" initialConfig={frameRate} setConstraints={setFrameRate} />
      <div className="px-4 py-2 flex flex-row-reverse sm:px-6">
        <button type="button" className={BUTTON_STYLE_MAIN_FLEX + ' m-1'} onClick={submit}>
          Submit
        </button>
        <button type="button" className={BUTTON_STYLE_CANCEL_FLEX + ' m-1'} onClick={cancel}>
          Cancel
        </button>
      </div>
    </TransitionModal>
  );
}
